import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import type { CertificateListResponse, CertificateResponse } from '@truecare/schemas';
import { Alert, Badge, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { api } from '../api';
import { ArtifactUploadModal } from './ArtifactUploadModal';

/**
 * Doc 16 — optional additional certificates (WHMIS, OHSA awareness, safe lifts,
 * GPA, … or Other). Shown under "Compliance documents".
 *
 * Purely optional: nothing here is required, reviewed or expiry-reminded, and it
 * never affects activation or which shifts are offered. Facilities see only the
 * certificate names + expiry on a booked shift; TrueCare sends the file itself
 * when a facility asks.
 *
 * Upload flow mirrors the compliance section: upload-url → PUT to storage →
 * record. The type picker + "Other" name ride in ArtifactUploadModal's
 * `leadingFields` slot.
 */
type CatalogItem = CertificateListResponse['catalog'][number];

const QUERY_KEY = ['caregiver', 'certificates'] as const;

export function CaregiverCertificatesSection({ readOnly = false }: { readOnly?: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [customName, setCustomName] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  // a11y — the Remove→confirm swap replaces the focused button; move focus to
  // the confirm button, and back to the row's Remove on "Keep", so keyboard
  // users aren't dropped to <body>.
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusTo = useRef<string | null>(null);
  useEffect(() => {
    if (confirmRemove) {
      confirmRef.current?.focus();
    } else if (returnFocusTo.current) {
      document
        .querySelector<HTMLButtonElement>(`[data-testid="certificate-remove-${returnFocusTo.current}"]`)
        ?.focus();
      returnFocusTo.current = null;
    }
  }, [confirmRemove]);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/certificates')),
  });

  const upload = useMutation({
    mutationFn: async (input: { file: File; issuedAt: string | null; expiresAt: string | null }) => {
      const urlResp = await unwrap(
        api.POST('/api/v1/me/caregiver/certificates/upload-url', {
          body: { contentType: input.file.type, contentLength: input.file.size },
        }),
      );
      const put = await fetch(urlResp.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': input.file.type },
        body: input.file,
      });
      if (!put.ok) {
        throw new Error(`Could not upload to storage (HTTP ${String(put.status)}). Try again.`);
      }
      return unwrap(
        api.POST('/api/v1/me/caregiver/certificates', {
          body: {
            code,
            ...(code === 'OTHER' ? { customName: customName.trim() } : {}),
            objectStorageKey: urlResp.objectStorageKey,
            ...(input.issuedAt ? { issuedAt: input.issuedAt } : {}),
            ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      closeAdd();
    },
    onError: (e) => setUploadError(messageOf(e, 'Upload failed. Try again.')),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE('/api/v1/me/caregiver/certificates/{id}', { params: { path: { id } } }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      setConfirmRemove(null);
    },
  });

  function closeAdd() {
    setAdding(false);
    setCode('');
    setCustomName('');
    setUploadError(null);
  }

  const header = (
    <CardHeader
      title="Additional certificates (optional)"
      description="Add any other certificates you hold — WHMIS, safe lifts & transfers, GPA, and so on. Optional: they never affect your activation or which shifts you're offered. On shifts you're booked for, facilities see the names of certificates from our list (ones you add under “Other” only as a count), and TrueCare can send the document if a facility asks."
    />
  );

  if (query.isPending) {
    return (
      <Card padding="lg">
        {header}
        <p className="mt-3 font-body text-sm text-ink-muted">Loading…</p>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card padding="lg">
        {header}
        <Alert tone="danger" title="Couldn't load your certificates" className="mt-3">
          Please refresh the page.
        </Alert>
      </Card>
    );
  }

  const { catalog, certificates } = query.data;
  const groups = groupCatalog(catalog);
  const nameMissing = code === 'OTHER' && customName.trim().length < 2;

  return (
    <>
      <Card padding="lg" data-testid="caregiver-certificates-section">
        {header}
        {remove.isError && (
          <Alert tone="danger" title="Couldn't remove" className="mt-3">
            {messageOf(remove.error)}
          </Alert>
        )}
        {certificates.length === 0 ? (
          <p className="mt-3 font-body text-sm text-ink-muted">No additional certificates added.</p>
        ) : (
          <ul className="mt-3 flex flex-col divide-y divide-border" data-testid="caregiver-certificates-list">
            {certificates.map((c: CertificateResponse) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="break-words font-body text-sm font-medium text-forest [overflow-wrap:anywhere]">
                      {c.displayName}
                    </span>
                    {c.isExpired && (
                      <Badge tone="danger" variant="soft">
                        Expired
                      </Badge>
                    )}
                  </div>
                  <p className="font-body text-xs text-ink-muted">{datesLine(c)}</p>
                </div>
                {!readOnly &&
                  (confirmRemove === c.id ? (
                    <div className="flex gap-2">
                      <Button
                        ref={confirmRef}
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(c.id)}
                        aria-label={`Confirm remove ${c.displayName}`}
                        data-testid={`certificate-remove-confirm-${c.id}`}
                      >
                        Remove
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Keep ${c.displayName}`}
                        onClick={() => {
                          returnFocusTo.current = c.id;
                          setConfirmRemove(null);
                        }}
                      >
                        Keep
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${c.displayName}`}
                      onClick={() => setConfirmRemove(c.id)}
                      data-testid={`certificate-remove-${c.id}`}
                    >
                      Remove
                    </Button>
                  ))}
              </li>
            ))}
          </ul>
        )}
        {!readOnly && (
          <div className="mt-4">
            <Button
              type="button"
              variant="secondary"
              // Not while an upload is still finishing: its success/error would
              // land on (and reset) a freshly reopened dialog.
              disabled={upload.isPending}
              onClick={() => setAdding(true)}
              data-testid="certificate-add"
            >
              Add a certificate
            </Button>
          </div>
        )}
      </Card>

      <ArtifactUploadModal
        open={adding}
        title="Add a certificate"
        error={uploadError}
        submitBlocked={code === '' || nameMissing}
        leadingFields={
          <>
            <Field label="Certificate">
              <select
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full rounded border border-ink-soft bg-paper px-2 py-1.5 font-body text-sm"
                data-testid="certificate-code"
              >
                <option value="">Choose a certificate…</option>
                {groups.map(([groupLabel, items]) => (
                  <optgroup key={groupLabel} label={groupLabel}>
                    {items.map((i) => (
                      <option key={i.code} value={i.code}>
                        {i.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
            {code === 'OTHER' && (
              <Field label="Certificate name" hint="For example, “Dementia care workshop”.">
                <Input
                  value={customName}
                  maxLength={120}
                  onChange={(e) => setCustomName(e.target.value)}
                  data-testid="certificate-custom-name"
                />
              </Field>
            )}
          </>
        }
        // Escape/Cancel are ignored while uploading (Cancel is already disabled
        // inside the dialog) so a closing dialog can't strand an in-flight upload.
        onCancel={() => {
          if (!upload.isPending) closeAdd();
        }}
        onSubmit={async (input) => {
          setUploadError(null);
          await upload.mutateAsync(input);
        }}
      />
    </>
  );
}

function groupCatalog(catalog: CatalogItem[]): [string, CatalogItem[]][] {
  const out = new Map<string, CatalogItem[]>();
  for (const item of catalog) {
    const list = out.get(item.groupLabel) ?? [];
    list.push(item);
    out.set(item.groupLabel, list);
  }
  return [...out.entries()];
}

function datesLine(c: CertificateResponse): string {
  const parts: string[] = [];
  if (c.issuedAt) parts.push(`Issued ${c.issuedAt}`);
  if (c.expiresAt) parts.push(`${c.isExpired ? 'Expired' : 'Expires'} ${c.expiresAt}`);
  return parts.length > 0 ? parts.join(' · ') : 'No expiry date';
}
